'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { fetchChildrenByEntity } from 'AdsUI/actions/CampaignReportingActions';
import { fetchColumnsWithEarlyRequester } from 'AdsUI/actions/ColumnActions';
import { fetchAllLifecycleStages } from 'AdsUI/actions/LifecycleStageActions';
import { EntityTypes, Networks, EntityChildren, EntityTypesByNetwork, ALL_STATUSES } from 'AdsUI/lib/Constants';
import { getAdAccounts, getAdAccountFromParams, getErrorsByAdAccount } from 'AdsUI/selectors/AccountsSelectors';
import { getChildrenFromProps, getCampaignTableAdGroup, getChildrenRequestStatus, getCampaignAdGroupRequestStatus, getEntityData, getCampaignForCampaignDetails, getCampaignRequestStatus, getCampaignChildrenTotals, getCampaignTableNoAdGroup } from 'AdsUI/selectors/CampaignReportingSelectors';
import { getToggledEntityStatus } from 'AdsUI/lib/Utils';
import { getLifecycleStageLabelsByStageId } from 'AdsUI/selectors/LifecycleStageSelectors';
import CampaignDetailsTable from 'AdsUI/components/campaign-details/CampaignDetailsTable';
import { updateReportingEntityStatus } from 'AdsUI/actions/CampaignReportingActions';
import { getSelectedColumns, getColumnsRequestStatus, getColumnDisplayLabels } from 'AdsUI/selectors/ColumnsSelectors';
import { getRequestStatus } from 'AdsUI/selectors/RequestStatusSelectors';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { hasWriteScope, hasAccessToRoiReporting } from 'AdsUI/selectors/AuthSelectors';
import NoParentTable from 'AdsUI/components/campaign-details/NoParentTable';
import { setColumns } from 'AdsUI/actions/ColumnActions';
import { getNetworkFromParams } from 'AdsUI/selectors/PropsSelectors';
import { withRouter } from 'react-router-dom';
import { getGlobalFilters } from 'AdsUI/selectors/GlobalFilters';
import { fetchAllWorkflowsForEntity } from 'AdsUI/actions/AdInteractionActions';
import { getSimpleWorkflows } from 'AdsUI/selectors/AutomationSelectors';
import Raven from 'raven-js';

const trackCacheStatus = (dataType, cacheHit) => {
  if (window.newrelic) {
    window.newrelic.addPageAction('ads-campaign-cache-event', {
      'ads-cache-hit': cacheHit,
      'ads-data-type': dataType
    });
  }
};

class CampaignDetailsTableContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.onReturnToCampaign = this.onReturnToCampaign.bind(this);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.toggleEntityStatus = this.toggleEntityStatus.bind(this);
    this.onColumnReorder = this.onColumnReorder.bind(this);
    this.onClickEntity = this.onClickEntity.bind(this);
  }

  componentDidMount() {
    this.resetTable();

    if (!this.props.columnOrderRequestStatus.fetched) {
      this.props.fetchColumns();
    }

    if (!this.props.lifecycleStagesRequestStatus.fetched) {
      this.props.fetchAllLifecycleStages();
    }

    trackCacheStatus('campaign-details-children', this.props.childRequestStatus.fetched);

    if (this.getParentType() === EntityTypes.ADGROUP) {
      trackCacheStatus('campaign-details-parent', this.props.adGroupRequestStatus.fetched);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      globalFilters
    } = this.props;

    if (globalFilters.initialized) {
      this.resetTable();
    }

    if (prevProps.globalFilters !== this.props.globalFilters || prevProps.entityType !== this.props.entityType) {
      trackCacheStatus('campaign-details-children', this.props.childRequestStatus.fetched);

      if (this.getParentType() === EntityTypes.ADGROUP) {
        trackCacheStatus('campaign-details-parent', this.props.adGroupRequestStatus.fetched);
      }
    }

    const entityIds = [];

    if (this.props.children) {
      this.props.children.forEach(entity => {
        entityIds.push(entity.getIn(['entity', 'id']));
      });
    }

    if (!this.props.workflowsForEntityRequestStatus.fetched && !this.props.workflowsForEntityRequestStatus.fetching && entityIds.length > 0) {
      this.props.fetchAllWorkflowsForEntity(this.props.network, this.props.adAccount.get('accountId'), this.props.entityType, entityIds).catch(error => Raven.captureException(error));
    }
  }

  resetTable() {
    const {
      network,
      match: {
        params: {
          accountId,
          campaignId,
          sequenceId
        }
      },
      entityType,
      entityId,
      globalFilters: {
        timeRange,
        attributionView,
        statuses
      },
      children,
      adGroupRequestStatus,
      childRequestStatus,
      noAdGroup
    } = this.props;

    if (!childRequestStatus.fetching && !childRequestStatus.fetchFailed && (!childRequestStatus.fetched || !children)) {
      // Refetch the children if they're not present, or if they haven't been fetched yet.
      // They won't be present even after being fetched if the data became stale (the
      // reducer for certain updates will remove old data from the state)
      const fetchEntityType = entityType === EntityTypes.NEGATIVE_KEYWORD ? EntityTypes.KEYWORD : entityType;
      const fetchStatuses = !!sequenceId || entityType === EntityTypes.NEGATIVE_KEYWORD ? ALL_STATUSES : statuses;
      this.props.fetchChildrenByEntity(network, accountId, campaignId, fetchEntityType, entityId, timeRange, attributionView, fetchStatuses);
    }

    if (this.getParentType() === EntityTypes.ADGROUP && !adGroupRequestStatus.fetched && !adGroupRequestStatus.fetching && !adGroupRequestStatus.fetchFailed && !(this.getParentType() === EntityTypes.ADGROUP && noAdGroup)) {
      this.props.fetchChildrenByEntity(network, accountId, campaignId, EntityTypes.ADGROUP, campaignId, timeRange, attributionView, ALL_STATUSES);
    }
  }

  getParentRequestStatus() {
    if (this.getParentType() === EntityTypes.CAMPAIGN) {
      return this.props.campaignRequestStatus;
    } else {
      return this.props.adGroupRequestStatus;
    }
  }

  getParent() {
    // The parent is either a campaign or an ad group
    const {
      entityType,
      campaign,
      adGroup
    } = this.props;

    if (entityType === EntityTypes.ADGROUP) {
      return campaign;
    } else {
      return adGroup;
    }
  }

  getParentType() {
    const {
      entityType
    } = this.props;

    if (entityType === EntityTypes.ADGROUP) {
      return EntityTypes.CAMPAIGN;
    } else {
      return EntityTypes.ADGROUP;
    }
  }

  onClickEntity(entity) {
    const {
      network
    } = this.props;
    const destinationEntityType = EntityTypesByNetwork[network].includes(EntityTypes.KEYWORD) ? EntityTypes.KEYWORD : EntityTypes.AD;
    this.onTypeChange(destinationEntityType, entity.id);
  }

  onReturnToCampaign() {
    const {
      match: {
        params: {
          network,
          accountId,
          campaignId
        }
      },
      history
    } = this.props;
    history.push(`/accounts/${network}/${accountId}/campaigns/${campaignId}`);
  }

  onTypeChange(newType, adGroupId) {
    const {
      match: {
        params: {
          sequenceId,
          network,
          accountId,
          campaignId
        }
      },
      history
    } = this.props;
    adGroupId = adGroupId || this.props.match.params.adGroupId;
    const sequencePath = sequenceId ? `/sequences/${sequenceId}` : '';

    switch (newType) {
      case EntityTypes.AD:
        {
          history.push(`${sequencePath}/accounts/${network}/${accountId}/campaigns/${campaignId}/adgroups/${adGroupId}`);
          break;
        }

      case EntityTypes.KEYWORD:
        {
          history.push(`/accounts/${network}/${accountId}/campaigns/${campaignId}/adgroups/${adGroupId}/keywords`);
          break;
        }

      case EntityTypes.NEGATIVE_KEYWORD:
        {
          history.push(`/accounts/${network}/${accountId}/campaigns/${campaignId}/adgroups/${adGroupId}/negative-keywords`);
          break;
        }

      default:
        {
          break;
        }
    }
  }

  toggleEntityStatus(entity) {
    const {
      network,
      adAccount,
      entityType
    } = this.props;
    const {
      campaignId
    } = this.props.match.params;
    usageTracker.track('modifyCampaign', {
      action: 'Toggled entity status',
      entityType,
      network
    });
    this.props.updateReportingEntityStatus(network, adAccount, entity, entityType, campaignId, this.getParent().entity, getToggledEntityStatus(entity.status, network));
  }

  onColumnReorder(columnToMove, locationColumn) {
    const {
      columns,
      entityType,
      network
    } = this.props;
    const newColumns = columns.delete(columns.indexOf(columnToMove)).insert(columns.indexOf(locationColumn), columnToMove);
    this.props.setColumns(entityType, network, newColumns.toArray());
    usageTracker.track('changeColumnOrder', {
      action: 'Dragged and dropped columns',
      grouping: entityType,
      network
    });
  }

  render() {
    const {
      CampaignDetailsBreadcrumbComponent,
      match: {
        params: {
          adGroupId,
          sequenceId
        }
      },
      children,
      entityType,
      network,
      adAccount,
      adAccounts,
      campaign,
      columnOrderRequestStatus,
      columns,
      lifecycleStageLabelsByStageId,
      globalFilters,
      campaignRequestStatus,
      childRequestStatus,
      hasWrite,
      hasRoiReporting,
      errorsByAdAccount,
      totals,
      noAdGroup,
      columnDisplayLabels,
      allWorkflows
    } = this.props;
    const parent = this.getParent();
    const possibleTypes = EntityChildren[this.getParentType()].filter(type => EntityTypesByNetwork[network].indexOf(type) !== -1);
    const parentRequestStatus = this.getParentRequestStatus();
    const loading = !(campaignRequestStatus.fetchedSuccessfully() && parentRequestStatus.fetchedSuccessfully() && childRequestStatus.fetchedSuccessfully() && columnOrderRequestStatus.fetchedSuccessfully());

    if (this.getParentType() === EntityTypes.ADGROUP && noAdGroup) {
      return /*#__PURE__*/_jsx(NoParentTable, {
        network: network,
        adGroupId: adGroupId,
        onReturnToCampaign: this.onReturnToCampaign
      });
    }

    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_jsx(CampaignDetailsTable, {
        CampaignDetailsBreadcrumbComponent: CampaignDetailsBreadcrumbComponent,
        loading: loading,
        failed: childRequestStatus.fetchFailed,
        network: network,
        columns: columns,
        lifecycleStageLabelsByStageId: lifecycleStageLabelsByStageId,
        adAccount: adAccount,
        adAccounts: adAccounts,
        campaignChildren: children,
        entityType: entityType,
        parent: parent && parent.entity,
        parentTrackingStatus: parent && parent.trackingStatus,
        parentType: this.getParentType(),
        possibleTypes: possibleTypes,
        campaignEntity: campaign.entity,
        globalFilters: globalFilters,
        statusFilter: !sequenceId && entityType !== EntityTypes.NEGATIVE_KEYWORD && globalFilters.statuses,
        onClickEntity: this.onClickEntity,
        onReturnToCampaign: this.onReturnToCampaign,
        onTypeChange: this.onTypeChange,
        toggleEntityStatus: this.toggleEntityStatus,
        hasWriteScope: hasWrite,
        hasRoiReportingScope: hasRoiReporting,
        onColumnReorder: this.onColumnReorder,
        errorsByAdAccount: errorsByAdAccount,
        totals: totals || parent && parent.report,
        hasCampaignManagement: network === Networks.FACEBOOK || network === Networks.LINKEDIN,
        columnDisplayLabels: columnDisplayLabels,
        allWorkflows: allWorkflows
      })
    });
  }

}

const mapStateToProps = (state, props) => {
  const network = getNetworkFromParams(state, props);
  const adAccount = getAdAccountFromParams(state, props);
  const {
    entityId,
    entityType
  } = getEntityData(state, props);
  return {
    network,
    entityId,
    entityType,
    adAccount,
    campaign: getCampaignForCampaignDetails(state, props),
    campaignRequestStatus: getCampaignRequestStatus(state, props),
    globalFilters: getGlobalFilters(state),
    children: getChildrenFromProps(state, props),
    adGroup: getCampaignTableAdGroup(state, props),
    noAdGroup: getCampaignTableNoAdGroup(state, props),
    columns: getSelectedColumns(state, {
      entityType,
      network
    }),
    lifecycleStageLabelsByStageId: getLifecycleStageLabelsByStageId(state),
    childRequestStatus: getChildrenRequestStatus(state, props),
    adGroupRequestStatus: getCampaignAdGroupRequestStatus(state, props),
    columnOrderRequestStatus: getColumnsRequestStatus(state),
    lifecycleStagesRequestStatus: getRequestStatus('lifecycleStages')(state),
    hasWrite: hasWriteScope(state),
    hasRoiReporting: hasAccessToRoiReporting(state),
    adAccounts: getAdAccounts(state),
    errorsByAdAccount: getErrorsByAdAccount(state),
    totals: getCampaignChildrenTotals(state, props),
    columnDisplayLabels: getColumnDisplayLabels(state),
    workflowsForEntityRequestStatus: getRequestStatus('automationWorkflows')(state),
    allWorkflows: getSimpleWorkflows(state)
  };
};

const mapDispatchToProps = {
  fetchChildrenByEntity,
  fetchColumns: fetchColumnsWithEarlyRequester,
  fetchAllLifecycleStages,
  updateReportingEntityStatus,
  setColumns,
  fetchAllWorkflowsForEntity
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CampaignDetailsTableContainer));